import React, { useEffect, useState } from 'react';

import {
  getDataSharingConsent,
  updateDataSharingConsent,
} from '../../services/districtsService';
import { Button } from '../common/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../common/Card';
import { ConfirmationModal } from '../common/ConfirmationModal';

import type { DataSharingConsent } from '../../services/districtsService';

interface DataSharingConsentSettingsProps {
  districtId: number;
}

const formatTimestamp = (value: string | null): string => {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

export const DataSharingConsentSettings: React.FC<
  DataSharingConsentSettingsProps
> = ({ districtId }) => {
  const [consent, setConsent] = useState<DataSharingConsent | null>(null);
  const [agreementVersion, setAgreementVersion] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showRevokeConfirmation, setShowRevokeConfirmation] = useState(false);

  useEffect(() => {
    let active = true;

    const loadConsent = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getDataSharingConsent(districtId);
        if (active) {
          setConsent(response);
          setAgreementVersion(response.agreement_version || '');
        }
      } catch (err) {
        if (active) {
          setError(
            err instanceof Error
              ? err.message
              : 'Unable to load data-sharing consent'
          );
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    loadConsent();
    return () => {
      active = false;
    };
  }, [districtId]);

  const recordConsent = async () => {
    const version = agreementVersion.trim();
    if (!version) {
      setError('Agreement version is required.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const response = await updateDataSharingConsent(districtId, {
        consent: true,
        agreement_version: version,
      });
      setConsent(response);
      setAgreementVersion(response.agreement_version || '');
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Unable to record consent'
      );
    } finally {
      setSaving(false);
    }
  };

  const revokeConsent = async () => {
    setSaving(true);
    setError(null);
    try {
      const response = await updateDataSharingConsent(districtId, {
        consent: false,
      });
      setConsent(response);
      setShowRevokeConfirmation(false);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Unable to revoke consent'
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-gray-600" role="status">
          Loading data-sharing consent…
        </CardContent>
      </Card>
    );
  }

  if (!consent) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-sm text-red-700" role="alert">
            {error || 'Data-sharing consent is unavailable.'}
          </p>
        </CardContent>
      </Card>
    );
  }

  const unchangedActiveVersion =
    consent.consent &&
    agreementVersion.trim() === consent.agreement_version;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Model-improvement data sharing</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div
            className={`rounded-md border p-4 ${
              consent.consent
                ? 'border-green-200 bg-green-50'
                : 'border-gray-200 bg-gray-50'
            }`}
          >
            <p
              className={`font-medium ${
                consent.consent ? 'text-green-900' : 'text-gray-900'
              }`}
            >
              {consent.consent ? 'Consent recorded' : 'Consent not recorded'}
            </p>
            <p className="mt-1 text-sm text-gray-700">
              Only data from districts with active consent may be included in
              future model-training exports.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <p className="text-sm text-gray-500">Agreement version</p>
              <p className="font-medium text-gray-900">
                {consent.agreement_version || 'Not recorded'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Signed</p>
              <p className="font-medium text-gray-900">
                {formatTimestamp(consent.signed_at)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Revoked</p>
              <p className="font-medium text-gray-900">
                {formatTimestamp(consent.revoked_at)}
              </p>
            </div>
          </div>

          <div>
            <label
              htmlFor="data-sharing-agreement-version"
              className="block text-sm font-medium text-gray-700"
            >
              Externally signed agreement version
            </label>
            <input
              id="data-sharing-agreement-version"
              value={agreementVersion}
              onChange={event => setAgreementVersion(event.target.value)}
              maxLength={50}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Example: DUA-2026-09"
            />
            <p className="mt-1 text-xs text-gray-500">
              This records a separately executed agreement; it does not create
              an electronic signature.
            </p>
          </div>

          {error && (
            <p className="text-sm text-red-700" role="alert">
              {error}
            </p>
          )}

          <div className="flex flex-wrap gap-3">
            <Button
              onClick={recordConsent}
              loading={saving}
              loadingText="Saving consent"
              disabled={!agreementVersion.trim() || unchangedActiveVersion}
            >
              {consent.consent
                ? 'Record updated agreement'
                : 'Record consent'}
            </Button>
            {consent.consent && (
              <Button
                variant="danger"
                onClick={() => setShowRevokeConfirmation(true)}
                disabled={saving}
              >
                Revoke consent
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <ConfirmationModal
        isOpen={showRevokeConfirmation}
        onClose={() => setShowRevokeConfirmation(false)}
        onConfirm={revokeConsent}
        title="Revoke data-sharing consent?"
        message="Revocation immediately blocks future exports and model training. Previously trained model artifacts that cannot be separated may be retained under the signed agreement."
        confirmText="Revoke consent"
        variant="danger"
        isLoading={saving}
      />
    </>
  );
};
